'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { HugeiconsIcon } from '@hugeicons/react'
import type { IconSvgElement } from '@hugeicons/react'
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Megaphone01Icon,
  HierarchyCircle03Icon,
  ImageIcon,
  ModernTvIcon,
  PlayIcon,
  SearchIcon,
  ShoppingCart01Icon,
  TextIcon,
  UserGroup03Icon,
  HeadphonesIcon,
} from '@hugeicons-pro/core-stroke-rounded'
import {
  CAMPAIGN_CHANNEL_PALETTE,
  parseDisplayNumber,
  type CampaignChannelKey,
  type CampaignRow,
  type CreativeRow,
} from '@/lib/campaigns/fallback-data'
import { cn } from '@/lib/utils'

export interface CampaignsTabsProps {
  campaigns: CampaignRow[]
  creatives: CreativeRow[]
  /** Optional pre-filter applied before tab counts + rendering. */
  channelFilter?: CampaignChannelKey
  /** Defaults to 'campaigns'. */
  defaultTab?: 'campaigns' | 'creatives'
  /** Optional className for the outer container. */
  className?: string
}

type SortKey = 'name' | 'channel' | 'status' | 'goal' | 'impressions' | 'spend' | 'cpm' | 'roas'

const CHANNEL_ICON: Record<CampaignChannelKey, IconSvgElement> = {
  search: SearchIcon,
  social: UserGroup03Icon,
  tv: ModernTvIcon,
  programmatic: HierarchyCircle03Icon,
  audio: HeadphonesIcon,
  retail: ShoppingCart01Icon,
  other: HierarchyCircle03Icon,
}

export function CampaignsTabs({
  campaigns,
  creatives,
  channelFilter,
  defaultTab = 'campaigns',
  className,
}: CampaignsTabsProps): JSX.Element {
  const [tab, setTab] = useState<'campaigns' | 'creatives'>(defaultTab)
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortAsc, setSortAsc] = useState(true)

  const filteredCampaigns = useMemo(() => {
    const filtered = channelFilter ? campaigns.filter((c) => c.channel === channelFilter) : campaigns
    if (!sortKey) return filtered
    const sorted = [...filtered].sort((a, b) => {
      const va = a[sortKey]
      const vb = b[sortKey]
      if (sortKey === 'impressions' || sortKey === 'spend' || sortKey === 'cpm' || sortKey === 'roas') {
        return numericSortValue(a, sortKey) - numericSortValue(b, sortKey)
      }
      return String(va).localeCompare(String(vb))
    })
    return sortAsc ? sorted : sorted.reverse()
  }, [campaigns, channelFilter, sortKey, sortAsc])

  const filteredCreatives = useMemo(
    () => channelFilter ? creatives.filter((creative) => creative.channel === channelFilter) : creatives,
    [channelFilter, creatives],
  )

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc((v) => !v)
    } else {
      setSortKey(key)
      setSortAsc(true)
    }
  }

  return (
    <div className={cn(className)}>
      <div className="bv3-cptabs">
        <button
          type="button"
          className={`bv3-cptab ${tab === 'campaigns' ? 'on' : ''}`}
          onClick={() => setTab('campaigns')}
        >
          <HugeiconsIcon icon={Megaphone01Icon} className="size-4 bv3-cptab-icon" />
          Campaigns
          <span className="n">{filteredCampaigns.length}</span>
        </button>
        <button
          type="button"
          className={`bv3-cptab ${tab === 'creatives' ? 'on' : ''}`}
          onClick={() => setTab('creatives')}
        >
          <HugeiconsIcon icon={PlayIcon} className="size-4 bv3-cptab-icon" />
          Creatives
          <span className="n">{filteredCreatives.length}</span>
        </button>
      </div>

      {tab === 'campaigns' ? (
        <>
          <div className="space-y-2.5 pt-3 md:hidden">
            {filteredCampaigns.length === 0 ? (
              <div className="rounded-card border-[0.5px] border-dashed border-line bg-surface px-4 py-8 text-center font-mono text-[13px] text-text-muted-2">
                No campaigns match these filters.
              </div>
            ) : (
              filteredCampaigns.map((c) => (
                <CampaignMobileCard key={c.id ?? c.name} campaign={c} />
              ))
            )}
          </div>

          <div className="hidden md:block">
            <table className="bv3-cptable">
              <thead>
                <tr>
                  <Th label="Campaign name" sortKey="name" sortBy={sortKey} sortAsc={sortAsc} onSort={toggleSort} />
                  <Th label="Channel" sortKey="channel" sortBy={sortKey} sortAsc={sortAsc} onSort={toggleSort} />
                  <Th label="Status" sortKey="status" sortBy={sortKey} sortAsc={sortAsc} onSort={toggleSort} />
                  <Th label="Goal" sortKey="goal" sortBy={sortKey} sortAsc={sortAsc} onSort={toggleSort} />
                  <Th label="Impr" sortKey="impressions" sortBy={sortKey} sortAsc={sortAsc} onSort={toggleSort} num />
                  <Th label="Spend" sortKey="spend" sortBy={sortKey} sortAsc={sortAsc} onSort={toggleSort} num />
                  <Th label="CPM" sortKey="cpm" sortBy={sortKey} sortAsc={sortAsc} onSort={toggleSort} num />
                  <Th label="ROAS" sortKey="roas" sortBy={sortKey} sortAsc={sortAsc} onSort={toggleSort} num />
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        padding: '24px 8px',
                        color: '#A8A69C',
                        textAlign: 'center',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      No campaigns match these filters.
                    </td>
                  </tr>
                ) : (
                  filteredCampaigns.map((c) => {
                    const palette = CAMPAIGN_CHANNEL_PALETTE[c.channel]
                    return (
                      <tr key={c.id ?? c.name}>
                        <td className="cname">
                          {c.id ? (
                            <Link href={`/campaigns/${c.id}`} className="hover:text-brand">
                              {c.name}
                            </Link>
                          ) : (
                            c.name
                          )}
                        </td>
                        <td>
                          <span className="chtag" style={{ background: palette.tint, color: palette.ink }}>
                            <HugeiconsIcon icon={CHANNEL_ICON[c.channel]} className="size-3.5" />
                            {palette.label}
                          </span>
                        </td>
                        <td>
                          <span className={`chtag ${c.status === 'Active' ? 'bv3-stat-active' : 'bv3-stat-paused'}`}>
                            {c.status}
                          </span>
                        </td>
                        <td>{c.goal}</td>
                        <td className="num">{c.impressions}</td>
                        <td className="num">{c.spend}</td>
                        <td className="num">{c.cpm}</td>
                        <td className="num" style={{ fontWeight: 700 }}>{c.roas}</td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="bv3-cvgrid">
          {filteredCreatives.map((cv) => {
            const palette = CAMPAIGN_CHANNEL_PALETTE[cv.channel]
            const icon =
              cv.format === 'Video' ? PlayIcon : cv.format === 'Image' ? ImageIcon : TextIcon
            const gradient = `linear-gradient(135deg, ${palette.hex}, ${palette.hex}CC)`
            return (
              <Link
                key={cv.id ?? cv.name}
                href={cv.id ? `/creatives/${cv.id}` : '#'}
                className={cn('bv3-cvc no-underline text-inherit transition-shadow hover:shadow-card', !cv.id && 'pointer-events-none')}
              >
                <div className="thumb" style={{ background: gradient }}>
                  <HugeiconsIcon icon={icon} className="size-8" style={{ color: '#fff' }} />
                </div>
                <div className="cvb">
                  <div className="cvn">{cv.name}</div>
                  <div className="cvm">{cv.format} · {palette.label}</div>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

function CampaignMobileCard({ campaign }: { campaign: CampaignRow }) {
  const palette = CAMPAIGN_CHANNEL_PALETTE[campaign.channel]
  const metrics = [
    { label: 'Impressions', value: campaign.impressions },
    { label: 'Spend', value: campaign.spend },
    { label: 'CPM', value: campaign.cpm },
    { label: 'ROAS', value: campaign.roas },
  ]
  const content = (
    <>
      <div
        className="flex items-center justify-between gap-2 px-4 py-3 text-white"
        style={{ background: palette.hex }}
      >
        <span className="min-w-0 flex-1 truncate text-[14px] font-extrabold leading-snug">
          {campaign.name}
        </span>
        <span className="inline-flex shrink-0 whitespace-nowrap rounded-full bg-white/25 px-2.5 py-1 text-[11px] font-bold">
          {campaign.status}
        </span>
      </div>
      <div className="rounded-b-card border border-t-0 border-line bg-surface px-4 py-3">
        <div className="mb-2 flex items-center gap-2 text-[11px] font-medium text-text-subtle">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-semibold"
            style={{ background: palette.tint, color: palette.ink }}
          >
            <HugeiconsIcon icon={CHANNEL_ICON[campaign.channel]} className="size-3.5" />
            {palette.label}
          </span>
          <span>{campaign.goal}</span>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {metrics.map((metric) => (
            <div key={metric.label}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted-2">
                {metric.label}
              </div>
              <div
                className="mt-0.5 font-mono text-[18px] font-extrabold tabular-nums"
                style={{ color: palette.ink }}
              >
                {metric.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )

  if (!campaign.id) {
    return (
      <div className="overflow-hidden rounded-card border-[0.5px] border-line">
        {content}
      </div>
    )
  }

  return (
    <Link
      href={`/campaigns/${campaign.id}`}
      className="block overflow-hidden rounded-card border-[0.5px] border-line no-underline shadow-sm transition-transform active:scale-[0.99]"
    >
      {content}
    </Link>
  )
}

function Th({
  label,
  sortKey,
  sortBy,
  sortAsc,
  onSort,
  num,
}: {
  label: string
  sortKey: SortKey
  sortBy: SortKey | null
  sortAsc: boolean
  onSort: (k: SortKey) => void
  num?: boolean
}) {
  const active = sortBy === sortKey
  return (
    <th
      className={`sortable ${num ? 'num' : ''}`}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active && (
        <HugeiconsIcon
          icon={sortAsc ? ArrowUp01Icon : ArrowDown01Icon}
          className="sortarrow inline-block"
        />
      )}
    </th>
  )
}

function numericSortValue(row: CampaignRow, key: SortKey): number {
  if (key === 'impressions') return Number(row.impressions_total ?? parseDisplayNumber(row.impressions))
  if (key === 'spend') return Number(row.spend_cents_total ?? parseDisplayNumber(row.spend) * 100)
  if (key === 'cpm') return Number(row.cpm_cents ?? parseDisplayNumber(row.cpm) * 100)
  if (key === 'roas') return Number(row.roas_raw ?? parseDisplayNumber(row.roas))
  return 0
}
